import React, { useEffect, useMemo, useRef, useState } from "react";
import { BarChart3, BadgeIndianRupee, Building2, CalendarDays, Check, ChevronDown, Eye, RefreshCw, ShoppingCart, Store, Truck } from "lucide-react";
import { restaurantAdminService, rowsOf, unwrap } from "../../services/restaurantAdmin.service";
import { Button, Card, EmptyState, ErrorBox, LoadingBox, PageHeader, TableShell, formatCurrency, idOf } from "./components/FoodAdminUi";
import { OrderDetailModal } from "./FoodOrdersPage";

const ORDER_REPORT_PAGE_SIZE = 5;

function first(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== "");
}

function money(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
}

function dateText(value) {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
}

function getReportPayload(res) {
  const data = unwrap(res, {});
  return data?.data && !Array.isArray(data.data) ? data.data : data;
}

function getSummary(report = {}) {
  return report.summary || report.totals || report.data?.summary || {};
}

function getOrders(report = {}) {
  if (Array.isArray(report.orders)) return report.orders;
  if (Array.isArray(report.rows)) return report.rows;
  if (Array.isArray(report.items)) return report.items;
  return [];
}

function getRestaurants(report = {}) {
  if (Array.isArray(report.restaurants)) return report.restaurants;
  if (Array.isArray(report.restaurantRows)) return report.restaurantRows;
  if (Array.isArray(report.restaurant_rows)) return report.restaurant_rows;
  return [];
}

function orderNo(order = {}) {
  return first(order.orderNumber, order.order_number, order.orderNo, order.order_no, order.id, "-");
}

function restaurantName(row = {}) {
  return first(row.restaurantName, row.restaurant_name, row.name, row.restaurant?.name, "-");
}

function financeOf(order = {}) {
  return order.financials || order.finance || {};
}

const REPORT_SCROLLBAR_STYLE = `
  .food-report-scrollbar {
    scrollbar-width: auto;
    scrollbar-color: auto;
  }
`;

function FilterDropdown({ label, value, onChange, options = [], searchable = false, className = "" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);

  useEffect(() => {
    function handlePointerDown(event) {
      if (!rootRef.current || rootRef.current.contains(event.target)) return;
      setOpen(false);
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  const selected = options.find((option) => String(option.value) === String(value));
  const selectedLabel = selected?.label || label || "Select";
  const filteredOptions = searchable && search.trim()
    ? options.filter((option) => String(option.label || "").toLowerCase().includes(search.trim().toLowerCase()))
    : options;

  function choose(nextValue) {
    onChange(nextValue);
    setOpen(false);
    setSearch("");
  }

  return (
    <div ref={rootRef} className={`relative min-w-0 ${className}`}>
      <button
        type="button"
        aria-label={label}
        onClick={() => setOpen((current) => !current)}
        className="flex h-11 w-full min-w-0 items-center justify-between gap-2 rounded-[14px] border border-[#f2c6cc] bg-white px-3 text-left text-[13px] font-medium text-slate-800 shadow-sm outline-none transition hover:bg-[#fffafb] focus:border-[#e86f7f] focus:ring-2 focus:ring-[#f8c7ce]"
      >
        <span className="truncate">{selectedLabel}</span>
        <ChevronDown size={16} className={`shrink-0 text-slate-400 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open ? (
        <div className="absolute right-0 z-40 mt-2 w-full min-w-[230px] overflow-hidden rounded-[16px] border border-[#f2c6cc] bg-white shadow-xl ring-1 ring-[#f9d5da]">
          {searchable ? (
            <div className="border-b border-[#f7d5d9] bg-[#fffafb] p-2">
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search restaurant"
                className="h-9 w-full rounded-[12px] border border-[#f2c6cc] bg-white px-3 text-[13px] outline-none focus:border-[#e86f7f]"
                autoFocus
              />
            </div>
          ) : null}

          <div className="food-report-scrollbar max-h-[260px] overflow-y-auto p-1">
            {filteredOptions.length ? filteredOptions.map((option) => {
              const active = String(option.value) === String(value);
              return (
                <button
                  key={`${option.value}-${option.label}`}
                  type="button"
                  onClick={() => choose(option.value)}
                  className={`flex w-full items-center justify-between gap-2 rounded-[12px] px-3 py-2 text-left text-[13px] transition ${active ? "bg-[#fff1f3] text-[#d84f62]" : "text-slate-700 hover:bg-[#fffafb]"}`}
                >
                  <span className="min-w-0 truncate">{option.label}</span>
                  {active ? <Check size={15} className="shrink-0" /> : null}
                </button>
              );
            }) : (
              <div className="px-3 py-4 text-center text-[13px] text-slate-400">No restaurant found</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ReportMetricCard({ icon, label, value, tone = "slate" }) {
  const tones = {
    slate: "from-white to-[#fff6f7] text-slate-800",
    green: "from-white to-emerald-50 text-slate-800",
    orange: "from-white to-orange-50 text-slate-800",
    blue: "from-white to-sky-50 text-slate-800",
  };

  return (
    <Card className={`min-w-0 ${tones[tone] || tones.slate}`}>
      <div className="flex min-w-0 items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white/75 text-slate-500 ring-1 ring-slate-100">
          {icon}
        </div>
        <div className="min-w-0">
          <div className="truncate text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</div>
          <div className={`mt-1 break-words text-[22px] font-semibold leading-tight ${tones[tone] || tones.slate}`}>{value}</div>
        </div>
      </div>
    </Card>
  );
}

function SectionHeading({ title, subtitle, right }) {
  return (
    <div className="mb-4 flex min-w-0 flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        <h2 className="text-[18px] font-semibold text-slate-900">{title}</h2>
        {subtitle ? <p className="mt-1 text-[12px] leading-relaxed text-slate-500">{subtitle}</p> : null}
      </div>
      {right ? <div className="shrink-0 text-[11px] font-medium uppercase tracking-wide text-slate-400">{right}</div> : null}
    </div>
  );
}

function AmountCell({ value, tone = "slate" }) {
  const tones = {
    slate: "text-slate-800",
    green: "text-emerald-700",
    orange: "text-orange-600",
    blue: "text-sky-700",
  };

  return <span className={`font-semibold ${tones[tone] || tones.slate}`}>{formatCurrency(value)}</span>;
}

function BreakdownRow({ label, value, className = "bg-slate-50 text-slate-700" }) {
  return (
    <div className={`flex flex-col gap-1 rounded-[14px] px-3 py-2 text-[13px] sm:flex-row sm:items-center sm:justify-between ${className}`}>
      <span className="font-medium">{label}</span>
      <span className="font-semibold">{formatCurrency(value)}</span>
    </div>
  );
}

export default function FoodReportsPage() {
  const [period, setPeriod] = useState("month");
  const [restaurantId, setRestaurantId] = useState("");
  const [restaurants, setRestaurants] = useState([]);
  const [report, setReport] = useState({});
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reportPage, setReportPage] = useState(1);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const [restaurantRes, reportRes] = await Promise.all([
        restaurantAdminService.listRestaurants({ limit: 300 }),
        restaurantAdminService.getRestaurantFinancialReport({ period, restaurantId, limit: 500 }),
      ]);
      setRestaurants(rowsOf(restaurantRes));
      setReport(getReportPayload(reportRes));
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [period, restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const summary = getSummary(report);
  const orders = getOrders(report);
  const restaurantRows = getRestaurants(report);
  const reportTotalPages = Math.max(1, Math.ceil(orders.length / ORDER_REPORT_PAGE_SIZE));
  const safeReportPage = Math.min(reportPage, reportTotalPages);
  const paginatedOrders = useMemo(
    () => orders.slice((safeReportPage - 1) * ORDER_REPORT_PAGE_SIZE, safeReportPage * ORDER_REPORT_PAGE_SIZE),
    [orders, safeReportPage]
  );
  const selectedOrder = useMemo(
    () => orders.find((order) => String(idOf(order) || order.orderId || order.order_id) === String(selectedOrderId)) || orders[0] || null,
    [orders, selectedOrderId]
  );

  useEffect(() => {
    setReportPage(1);
  }, [period, restaurantId]);

  useEffect(() => {
    if (reportPage > reportTotalPages) setReportPage(reportTotalPages);
  }, [reportPage, reportTotalPages]);

  async function openOrderDetail(orderId) {
    if (!orderId) return;
    setSelectedOrderId(orderId);
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    setError("");
    try {
      const res = await restaurantAdminService.getFoodOrder(orderId);
      setDetail(unwrap(res));
    } catch (e) {
      setError(e);
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  }

  if (loading) return <LoadingBox label="Loading reports..." />;

  return (
    <div className="min-w-0">
      <style>{REPORT_SCROLLBAR_STYLE}</style>
      <PageHeader
        // eyebrow="Reports"
        // icon={<BarChart3 size={14} />}
        title="Reports"
        subtitle="Filter by restaurant and period. Shows customer total, restaurant earning, admin commission, driver amount and platform-side Small Cart Charge separately."
        actions={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <FilterDropdown
              label="Period"
              value={period}
              onChange={setPeriod}
              className="w-full sm:w-[130px]"
              options={[
                { value: "day", label: "Day" },
                { value: "week", label: "Week" },
                { value: "month", label: "Month" },
                { value: "year", label: "Year" },
              ]}
            />
            <FilterDropdown
              label="Restaurant"
              value={restaurantId}
              onChange={setRestaurantId}
              searchable
              className="w-full sm:w-[240px]"
              options={[
                { value: "", label: "All Restaurants" },
                ...restaurants.map((restaurant) => ({ value: idOf(restaurant), label: restaurantName(restaurant) })),
              ]}
            />
            <Button variant="ghost" onClick={load}><RefreshCw size={16} /> Refresh</Button>
          </div>
        }
      />

      <ErrorBox error={error} onRetry={load} />

      <div className="mb-5 grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-6">
        <ReportMetricCard icon={<BadgeIndianRupee size={18} />} label="Customer Total" value={formatCurrency(first(summary.totalCustomerAmount, summary.revenue, 0))} />
        <ReportMetricCard icon={<Store size={18} />} label="Restaurant Earned" value={formatCurrency(first(summary.restaurantAmount, summary.restaurantPayout, 0))} tone="green" />
        <ReportMetricCard icon={<BadgeIndianRupee size={18} />} label="Admin Cut" value={formatCurrency(first(summary.adminAmount, summary.adminCommissionAmount, 0))} tone="orange" />
        <ReportMetricCard icon={<ShoppingCart size={18} />} label="Small Cart Charge" value={formatCurrency(first(summary.smallCartCharge, 0))} tone="orange" />
        <ReportMetricCard icon={<Truck size={18} />} label="Driver Amount" value={formatCurrency(first(summary.driverAmount, summary.driverEarning, 0))} tone="blue" />
        <ReportMetricCard icon={<CalendarDays size={18} />} label="Orders" value={first(summary.orderCount, summary.totalOrders, orders.length, 0)} />
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(320px,420px)]">
        <div className="min-w-0 space-y-5">
          <Card className="min-w-0 overflow-hidden">
            <SectionHeading
              title="Restaurant-wise totals"
              subtitle="Restaurant earning = food amount after admin cut + packaging. Admin cut is calculated from commission percent."
              right={`${period} view`}
            />
            {restaurantRows.length ? (
              <div className="food-report-scrollbar max-h-[360px] overflow-auto pr-1">
                <TableShell columns={["Restaurant", "Orders", "Customer Total", "Restaurant", "Admin", "Small Cart", "Driver", "Tax"]} minWidth={1020}>
                  {restaurantRows.map((row) => (
                    <tr key={first(row.restaurantId, row.restaurant_id, row.id, restaurantName(row))} className="border-b last:border-0 hover:bg-[#fffafb]">
                      <td className="px-4 py-4 font-medium text-slate-900">{restaurantName(row)}</td>
                      <td className="px-4 py-4 font-medium text-slate-700">{first(row.orders, row.order_count, 0)}</td>
                      <td className="px-4 py-4"><AmountCell value={first(row.totalCustomerAmount, row.revenue, 0)} /></td>
                      <td className="px-4 py-4"><AmountCell value={first(row.restaurantAmount, row.restaurantPayout, 0)} tone="green" /></td>
                      <td className="px-4 py-4"><AmountCell value={first(row.adminAmount, row.adminCommissionAmount, 0)} tone="orange" /></td>
                      <td className="px-4 py-4"><AmountCell value={first(row.smallCartCharge, 0)} tone="orange" /></td>
                      <td className="px-4 py-4"><AmountCell value={first(row.driverAmount, row.driverEarning, 0)} tone="blue" /></td>
                      <td className="px-4 py-4 text-slate-700">{formatCurrency(first(row.taxAmount, 0))}</td>
                    </tr>
                  ))}
                </TableShell>
              </div>
            ) : (
              <EmptyState title="No restaurant totals" subtitle="Orders for this period will appear here." icon={<Building2 size={22} />} />
            )}
          </Card>

          <Card className="min-w-0 overflow-hidden">
            <SectionHeading
              title="Order-wise earning details"
              subtitle="Every order shows restaurant share, admin cut, driver amount and other charges."
            />
            {orders.length ? (
              <div className="food-report-scrollbar max-h-[560px] overflow-auto pr-1">
                <TableShell columns={["Order", "Restaurant", "Customer", "Food", "Restaurant", "Admin", "Driver", "Other", "Action"]} minWidth={1120}>
                  {paginatedOrders.map((order) => {
                    const f = financeOf(order);
                    const other = money(first(f.taxAmount, order.taxAmount, order.tax_amount, 0)) + money(first(f.packagingCharge, order.packagingCharge, order.packaging_charge, 0)) + money(first(f.deliveryFee, order.deliveryFee, order.delivery_fee, 0)) + money(first(f.smallCartCharge, order.smallCartCharge, 0));
                    const oid = idOf(order) || order.orderId || order.order_id;
                    return (
                      <tr key={oid} className="border-b last:border-0 hover:bg-[#fffafb]">
                        <td className="px-4 py-4">
                          <div className="font-medium text-slate-900">{orderNo(order)}</div>
                          <div className="mt-1 text-[11px] text-slate-400">{dateText(first(order.createdAt, order.created_at))}</div>
                        </td>
                        <td className="px-4 py-4 font-medium text-slate-800">{restaurantName(order)}</td>
                        <td className="px-4 py-4">
                          <div className="font-medium text-slate-800">{first(order.customerName, order.customer_name, order.address?.name, "-")}</div>
                          <div className="mt-1 text-[11px] text-slate-400">{first(order.customerPhone, order.customer_phone, order.address?.phone, "")}</div>
                        </td>
                        <td className="px-4 py-4"><AmountCell value={first(f.foodSubtotal, order.subtotal, 0)} /></td>
                        <td className="px-4 py-4"><AmountCell value={first(f.restaurantAmount, order.restaurantAmount, 0)} tone="green" /></td>
                        <td className="px-4 py-4"><AmountCell value={first(f.adminAmount, f.adminCommissionAmount, order.adminAmount, 0)} tone="orange" /></td>
                        <td className="px-4 py-4"><AmountCell value={first(f.driverAmount, order.driverAmount, 0)} tone="blue" /></td>
                        <td className="px-4 py-4 text-slate-700">{formatCurrency(other)}</td>
                        <td className="px-4 py-4"><Button variant="ghost" size="sm" onClick={() => openOrderDetail(oid)}><Eye size={14} /> View</Button></td>
                      </tr>
                    );
                  })}
                </TableShell>
                <div className="mt-4 flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
                  <button
                    type="button"
                    disabled={safeReportPage === 1}
                    onClick={() => setReportPage((prev) => Math.max(1, prev - 1))}
                    className="inline-flex h-9 items-center justify-center rounded-[10px] border border-[#f2c6cc] bg-white px-4 text-[12px] font-semibold text-[#d86a78] transition hover:bg-[#fff8f9] disabled:cursor-not-allowed disabled:opacity-45"
                  >
                    Previous
                  </button>

                  <div className="inline-flex h-9 items-center justify-center rounded-[10px] border border-[#f2c6cc] bg-[#fff8f9] px-4 text-[12px] font-semibold text-[#d86a78]">
                    Page {safeReportPage} / {reportTotalPages}
                  </div>

                  <button
                    type="button"
                    disabled={safeReportPage === reportTotalPages}
                    onClick={() => setReportPage((prev) => Math.min(reportTotalPages, prev + 1))}
                    className="inline-flex h-9 items-center justify-center rounded-[10px] border border-[#f2c6cc] bg-white px-4 text-[12px] font-semibold text-[#d86a78] transition hover:bg-[#fff8f9] disabled:cursor-not-allowed disabled:opacity-45"
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : (
              <EmptyState title="No report orders" subtitle="No food orders found for selected filters." icon={<BarChart3 size={22} />} />
            )}
          </Card>
        </div>

        <Card className="min-w-0 xl:sticky xl:top-4 xl:self-start">
          <div className="food-report-scrollbar max-h-[70vh] overflow-y-auto pr-1 xl:max-h-[calc(100vh-7rem)]">
          <div className="mb-4 min-w-0">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Selected order breakdown</div>
            <h3 className="mt-1 break-words text-lg font-semibold text-slate-900">{selectedOrder ? orderNo(selectedOrder) : "No order"}</h3>
          </div>

          {selectedOrder ? (
            <div className="space-y-3 text-[13px]">
              <BreakdownRow label="Customer paid" value={first(financeOf(selectedOrder).totalFromCustomer, selectedOrder.grandTotal, 0)} className="border text-slate-800" />
              <BreakdownRow label="Restaurant food amount" value={first(financeOf(selectedOrder).restaurantFoodAmount, 0)} className="border text-slate-800" />
              <BreakdownRow label="Packaging to restaurant" value={first(financeOf(selectedOrder).packagingCharge, 0)} className="border  text-slate-800" />
              <BreakdownRow label="Final restaurant earning" value={first(financeOf(selectedOrder).restaurantAmount, 0)} className="border text-slate-800" />
              <BreakdownRow label={`Admin cut (${first(financeOf(selectedOrder).commissionPercent, selectedOrder.commissionPercent, 0)}%)`} value={first(financeOf(selectedOrder).adminAmount, financeOf(selectedOrder).adminCommissionAmount, 0)} className="border text-slate-800" />
              <BreakdownRow label="Driver amount" value={first(financeOf(selectedOrder).driverAmount, 0)} className="border text-slate-800" />
              <BreakdownRow label="Small Cart Charge (platform)" value={first(financeOf(selectedOrder).smallCartCharge, selectedOrder.smallCartCharge, 0)} className="border text-slate-800" />
              <BreakdownRow label="Tax" value={first(financeOf(selectedOrder).taxAmount, 0)} className="border  text-slate-800" />
              <BreakdownRow label="Discount" value={first(financeOf(selectedOrder).discountAmount, 0)} className="bg-rose-50 text-rose-700" />

              <div className="rounded-[16px] border border-[#f2c6cc] p-4">
                <div className="mb-2 text-[12px] font-medium uppercase tracking-wide text-slate-400">Restaurant</div>
                <div className="break-words font-semibold text-slate-900">{restaurantName(selectedOrder)}</div>
                {/* <div className="mt-1 break-words text-[12px] text-slate-500">ID: {first(selectedOrder.restaurantId, selectedOrder.restaurant_id, "-")}</div> */}
              </div>
            </div>
          ) : (
            <EmptyState title="No order selected" subtitle="Select any order to view its split." icon={<Eye size={22} />} />
          )}
          </div>
        </Card>
      </div>

      {detailOpen ? (
        <OrderDetailModal
          order={detail}
          loading={detailLoading}
          onClose={() => {
            setDetailOpen(false);
            setDetail(null);
          }}
        />
      ) : null}
    </div>
  );
}
